import React from 'react';
import { TrendingUp, TrendingDown, Award, Target, MapPin, FileText } from 'lucide-react';

const ComplianceScoring = () => {
  // Mock scoring data
  const overallScore = 87;
  const previousScore = 82;
  const scoreChange = overallScore - previousScore;

  const regulationTypes = [
    { name: 'Safety Compliance', score: 92, total: 100, color: 'bg-green-500' },
    { name: 'Environmental', score: 85, total: 100, color: 'bg-blue-500' },
    { name: 'Health & Hygiene', score: 88, total: 100, color: 'bg-purple-500' },
    { name: 'Fire Safety', score: 90, total: 100, color: 'bg-orange-500' },
    { name: 'Documentation', score: 78, total: 100, color: 'bg-yellow-500' }
  ];

  const locationScores = [
    { location: 'Plant A', score: 92, status: 'excellent' },
    { location: 'Plant B', score: 85, status: 'good' },
    { location: 'Warehouse C', score: 78, status: 'fair' },
    { location: 'Office HQ', score: 95, status: 'excellent' },
    { location: 'Plant D', score: 82, status: 'good' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'good':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'fair':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'poor':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall Compliance Score */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Overall Compliance Score</h2>
            <p className="text-sm text-gray-600 mt-1">Real-time regulatory readiness assessment</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Generate Report
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Score Gauge */}
          <div className="md:col-span-1">
            <div className="relative w-48 h-48 mx-auto">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="80"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="16"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="80"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="16"
                  strokeDasharray={`${(overallScore / 100) * 502.4} 502.4`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <div className="text-4xl font-bold text-gray-900">{overallScore}%</div>
                <div className={`flex items-center gap-1 mt-2 ${scoreChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {scoreChange >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">
                    {Math.abs(scoreChange)}% vs last month
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="md:col-span-2 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Completed</span>
                </div>
                <p className="text-2xl font-bold text-green-900">42/48</p>
                <p className="text-xs text-green-700 mt-1">Compliance milestones</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">On Track</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">12</p>
                <p className="text-xs text-blue-700 mt-1">Active requirements</p>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-900">Pending Review</span>
                </div>
                <p className="text-2xl font-bold text-yellow-900">8</p>
                <p className="text-xs text-yellow-700 mt-1">Documents awaiting approval</p>
              </div>

              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-900">At Risk</span>
                </div>
                <p className="text-2xl font-bold text-red-900">4</p>
                <p className="text-xs text-red-700 mt-1">Items requiring attention</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regulation Type Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Compliance by Regulation Type
          </h3>
          <div className="space-y-4">
            {regulationTypes?.map((regulation, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{regulation?.name}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {regulation?.score}/{regulation?.total}
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${regulation?.color} transition-all duration-300`}
                    style={{ width: `${(regulation?.score / regulation?.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Location Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Location-Specific Performance
          </h3>
          <div className="space-y-3">
            {locationScores?.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{location?.location}</p>
                    <span className={`inline-block text-xs px-2 py-1 rounded border mt-1 ${getStatusColor(location?.status)}`}>
                      {location?.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{location?.score}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Recommendations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Improvement Recommendations</h3>
        <div className="space-y-3">
          <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Documentation Completeness</p>
              <p className="text-sm text-gray-600 mt-1">
                Review and update 5 expired safety procedure documents at Warehouse C
              </p>
            </div>
          </div>

          <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Training Requirements</p>
              <p className="text-sm text-gray-600 mt-1">
                Schedule refresher training for 8 employees whose certifications expire within 30 days
              </p>
            </div>
          </div>

          <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Best Practice Implementation</p>
              <p className="text-sm text-gray-600 mt-1">
                Plant A's safety protocols can be replicated at Plant B to improve compliance score by 7%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceScoring;